import { useState } from "react";

import { FormActions } from "@/components/layout/form-actions";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const MAX_ADD_ROWS = 500;

interface AddRowsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAddRows: (count: number) => void;
}

function parseRowCount(input: string) {
  const count = Number(input.trim());
  return Number.isInteger(count) && count >= 1 && count <= MAX_ADD_ROWS ? count : null;
}

/** Asks how many blank rows to append to the submittal grid. */
export function AddRowsDialog({ open, onOpenChange, onAddRows }: AddRowsDialogProps) {
  const [value, setValue] = useState("1");
  const count = parseRowCount(value);
  const showError = value.trim() !== "" && count === null;

  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange(nextOpen);
    if (!nextOpen) setValue("1");
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Add Blank Rows</DialogTitle>
          <DialogDescription>Rows are added to the end of the submittal.</DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (count === null) return;
            onAddRows(count);
            handleOpenChange(false);
          }}
        >
          <div className="space-y-2">
            <Label htmlFor="add-rows-count">Number of rows</Label>
            <Input
              id="add-rows-count"
              type="number"
              inputMode="numeric"
              min={1}
              max={MAX_ADD_ROWS}
              step={1}
              value={value}
              onChange={(event) => setValue(event.target.value)}
              onFocus={(event) => event.currentTarget.select()}
              aria-invalid={showError || undefined}
              aria-describedby={showError ? "add-rows-error" : undefined}
              autoFocus
            />
            {showError ? (
              <p id="add-rows-error" className="text-xs text-destructive">
                Enter a whole number from 1 to {MAX_ADD_ROWS}.
              </p>
            ) : null}
          </div>
          <FormActions
            onCancel={() => handleOpenChange(false)}
            submitLabel="Add Rows"
            submitDisabled={count === null}
          />
        </form>
      </DialogContent>
    </Dialog>
  );
}
